import React, { useState, useEffect } from 'react';
import { Shield, Sword, Heart } from 'lucide-react';

interface CastleDefenseProps {
  difficulty: 'easy' | 'medium' | 'hard';
  onWin: () => void;
  onLose: () => void;
}

export function CastleDefense({ difficulty, onWin, onLose }: CastleDefenseProps) {
  const [castleHealth, setCastleHealth] = useState(100);
  const [enemyCount, setEnemyCount] = useState(50);
  const [turnCount, setTurnCount] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);

  // Difficulty modifiers
  const difficultyModifiers = {
    easy: { enemyDamage: 0.8, playerDamage: 1.2 },
    medium: { enemyDamage: 1.0, playerDamage: 1.0 },
    hard: { enemyDamage: 1.2, playerDamage: 0.8 }
  };

  const handleAction = (action: 'attack' | 'repair') => {
    if (gameOver) return;

    const mod = difficultyModifiers[difficulty];
    
    if (action === 'attack') {
      const killedEnemies = Math.floor((Math.random() * 10 + 5) * mod.playerDamage);
      setEnemyCount(prev => Math.max(0, prev - killedEnemies));
      setMessage(`You defeated ${killedEnemies} enemies!`);
    } else {
      const repairAmount = Math.floor((Math.random() * 10 + 10));
      setCastleHealth(prev => Math.min(100, prev + repairAmount));
      setMessage(`Repaired castle for ${repairAmount} health!`);
    }

    // Enemy turn
    if (enemyCount > 0) {
      const damage = Math.floor((Math.random() * 15 + 10) * mod.enemyDamage);
      setTimeout(() => {
        setCastleHealth(prev => Math.max(0, prev - damage));
        setMessage(prev => `${prev}\nEnemies deal ${damage} damage!`);
      }, 500);
    }

    setTurnCount(prev => prev + 1);
  };

  useEffect(() => {
    if (castleHealth <= 0) {
      setGameOver(true);
      onLose();
    } else if (enemyCount <= 0) {
      setGameOver(true);
      onWin();
    }
  }, [castleHealth, enemyCount, onWin, onLose]);

  if (!isPlaying) {
    return (
      <div className="text-center space-y-6">
        <h2 className="text-2xl font-bold text-white mb-4">Castle Defense</h2>
        <p className="text-gray-300 mb-6">
          Defend your castle against waves of enemies! Choose your actions wisely between attacking enemies and repairing your castle.
        </p>
        <button
          onClick={() => setIsPlaying(true)}
          className="px-6 py-3 bg-gradient-to-r from-yellow-600 to-amber-500 text-white rounded-lg hover:from-yellow-500 hover:to-amber-400"
        >
          Start Battle
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Status Display */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-gray-800 p-4 rounded-lg text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Heart className="w-5 h-5 text-red-400" />
            <span className="text-gray-300">Castle Health</span>
          </div>
          <div className="text-2xl font-bold text-red-400">{castleHealth}</div>
          <div className="w-full h-2 bg-gray-700 rounded-full mt-2">
            <div 
              className="h-full bg-red-400 rounded-full transition-all"
              style={{ width: `${castleHealth}%` }}
            />
          </div>
        </div>

        <div className="bg-gray-800 p-4 rounded-lg text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Sword className="w-5 h-5 text-yellow-400" />
            <span className="text-gray-300">Enemies</span>
          </div>
          <div className="text-2xl font-bold text-yellow-400">{enemyCount}</div>
          <div className="w-full h-2 bg-gray-700 rounded-full mt-2">
            <div 
              className="h-full bg-yellow-400 rounded-full transition-all"
              style={{ width: `${(enemyCount / 50) * 100}%` }}
            />
          </div>
        </div>

        <div className="bg-gray-800 p-4 rounded-lg text-center">
          <div className="flex items-center justify-center gap-2 mb-2">
            <Shield className="w-5 h-5 text-cyan-400" />
            <span className="text-gray-300">Turn</span>
          </div>
          <div className="text-2xl font-bold text-cyan-400">{turnCount}</div>
        </div>
      </div>

      {/* Message Log */}
      {message && (
        <div className="bg-gray-800 p-4 rounded-lg">
          <pre className="text-gray-300 whitespace-pre-wrap font-mono">{message}</pre>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-4">
        <button
          onClick={() => handleAction('attack')}
          disabled={gameOver}
          className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-red-500 hover:bg-red-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Sword className="w-5 h-5" />
          Attack
        </button>
        <button
          onClick={() => handleAction('repair')}
          disabled={gameOver || castleHealth >= 100}
          className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-green-500 hover:bg-green-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Shield className="w-5 h-5" />
          Repair
        </button>
      </div>

      {/* Game Over Message */}
      {gameOver && (
        <div className="text-center">
          <p className="text-xl font-bold">
            {castleHealth <= 0 ? (
              <span className="text-red-400">Castle has fallen!</span>
            ) : (
              <span className="text-green-400">Victory! All enemies defeated!</span>
            )}
          </p>
        </div>
      )}
    </div>
  );
}